

















































import Vue from 'vue';
import Component from 'vue-class-component';
import { Prop, Ref } from 'vue-property-decorator';
import { BotProtClient } from '@/utils/botProt';
import { getQueueItHeader } from '@/utils/queue_it';

@Component
export default class BotProt extends Vue {
    @Ref('botprot-dialog')
    dialog!: HTMLDialogElement;

    @Ref('botprot-widget')
    widgetContainer!: HTMLElement;

    @Prop({ required: true })
    shopId!: string;

    @Prop({ required: true })
    botProtClient!: BotProtClient;

    private showBotProt = false;

    async mounted() {
        const data: Record<string, unknown> = {};

        try {
            const [
                queueItKey,
                queueItValue,
            ] = await this.waitForQueueItConfig();

            switch (queueItKey) {
                case 'x-queueit-ajaxpageurl':
                    data['queueit-ajaxpageurl'] = queueItValue;
                    break;
                case 'x-queueit-cookies':
                    data['queueit-cookies'] = queueItValue;
                    break;
                default:
                    /* No-op */
                    break;
            }
        } catch (error) {
            console.warn('BotProt initialization without queue-it:', error);
        }

        this.showBotProt = await this.botProtClient.init(this.shopId, data);

        if (!this.showBotProt) {
            this.$emit('botprot-disabled');
            return false;
        }

        const resumeSessionUrl = this.resumeSessionUrl();

        this.botProtClient.onFinal = (jwt: string | undefined) => {
            this.$emit('verified', { jwt });

            if (this.dialog.open) {
                this.dialog.close();
            }
        };

        this.dialog.showModal();

        this.$nextTick(() => {
            let spinnerImage = this.$whitelabel.resources.spinner;

            if (this.$style.getAppliedTheme() == 'dark') {
                spinnerImage = this.$whitelabel.resources.spinnerDark;
            }

            this.botProtClient.render(
                this.widgetContainer,
                `<div><img src="${spinnerImage}" style="height: 3rem" alt="Loading"></div>`,
                resumeSessionUrl
            );
        });
    }

    /**
     * Waits for the Queue-it integration to be ready, up to a timeout
     * Needed to avoid a race condition between the Queue-it integration and the botprot init
     * @param timeoutMs
     * @param intervalMs
     */
    async waitForQueueItConfig(
        timeoutMs = 5000,
        intervalMs = 100
    ): Promise<[string, string]> {
        return new Promise((resolve, reject) => {
            const startTime = Date.now();

            const intervalId = setInterval(() => {
                const queueItValues = getQueueItHeader();
                if (queueItValues) {
                    clearInterval(intervalId);
                    resolve(queueItValues);
                } else if (Date.now() - startTime > timeoutMs) {
                    clearInterval(intervalId);
                    reject(new Error('QueueIt configuration time out'));
                }
            }, intervalMs);
        });
    }

    resumeSessionUrl(): string | undefined {
        const { botprot_next } = this.$route.query;

        if (!botprot_next) {
            return undefined;
        }

        // Intentionally not awaited, as the widget should render
        // regardless of whether the query param was stripped.
        void this.stripResumeSessionQueryParam();

        if (typeof botprot_next === 'string') {
            return botprot_next;
        }

        if (Array.isArray(botprot_next)) {
            return botprot_next.filter(
                (v: string | null): v is string => !!v
            )[0];
        }

        return undefined;
    }

    /**
     * Removes the botprot_next query param from the url, to prevent
     * the resume session from being re-used on a reload or a share.
     *
     * Navigation errors - e.g., when another navigation is already
     * in flight - are non-critical and are swallowed.
     */
    async stripResumeSessionQueryParam(): Promise<void> {
        try {
            const query = { ...this.$route.query };
            delete query['botprot_next'];

            await this.$router.replace({ query });
        } catch (e) {
            console.warn('Failed to strip botprot_next query param', { e });
        }
    }
}
